import { createFileRoute } from "@tanstack/react-router";
import { ExternalLink } from "@/components/primitives/external-link";
import { BreadcrumbHeader } from "@/components/primitives/breadcrumb-header";
import { UnderlineLink } from "@/components/primitives/underline-link";
import {
  Article,
  ArticleAside,
  ArticleBody,
  ArticleContainer,
  ArticleDescription,
  ArticleHeader,
  ArticleHeading,
  ArticleList,
  ArticleListItem,
  ArticleParagraph,
  ArticleSection,
  ArticleSectionHeader,
} from "@/components/primitives/article";
import { FpdfCalculator } from "@/components/views/learn/fpdf-calculator";
import { Suspense } from "react";
import { formatGrams, formatPercentage } from "@/lib/formatters";
import { PageContainer } from "@/components/primitives/layout";
import { SingleBreadcrumb } from "@/components/primitives/single-breadcrumb";

const TITLE = "How to calculate FPDF (PAC)";
const DESCRIPTION =
  "Learn the science behind making soft, scoopable ice cream.";

export const Route = createFileRoute("/learn/fpdf")({
  head: () => ({
    meta: [
      { title: `${TITLE} - Scoopulator` },
      { name: "description", content: DESCRIPTION },
    ],
  }),
  component: PACCalculatorPage,
});

function PACCalculatorPage() {
  return (
    <PageContainer>
      <ArticleContainer>
        <BreadcrumbHeader>
          <SingleBreadcrumb href="/learn" label="Learn more about ice cream" />
        </BreadcrumbHeader>
        <div className="space-y-8">
          <Article>
            <ArticleHeader>
              <ArticleHeading>How to calculate FPDF (PAC)</ArticleHeading>
              <ArticleDescription>
                Learn the science behind making soft, scoopable ice cream.
              </ArticleDescription>
            </ArticleHeader>

            <ArticleBody>
              <ArticleSection>
                <ArticleSectionHeader id="what-is-fpdf">
                  What is FPDF?
                </ArticleSectionHeader>
                <ArticleParagraph>
                  <strong>FPDF</strong> (Freezing Point Depression Factor) is
                  also known as <strong>PAC</strong> (Potere Anti-Congelante,
                  italian for Anti-Freezing Power). It measures an
                  ingredient&apos;s ability to lower the freezing point of water
                  in ice cream. Without freezing point depression, ice cream
                  would be solid as a block of ice.
                </ArticleParagraph>
                <ArticleParagraph>
                  The most common way to make ice cream softer is by adding
                  <UnderlineLink
                    to="/ingredients/$slug"
                    params={{ slug: "usdaff-746784" }}
                  >
                    table sugar
                  </UnderlineLink>
                  . This works to an extent, but often results in ice cream
                  recipes which are too sweet. This can be remedied by utilizing
                  ingredients with more anti-freezing power. In order to create
                  a balanced recipe with great texture, it&apos;s important to
                  know the FPDF of all ingredients.
                </ArticleParagraph>
                <ArticleParagraph>
                  To compare the anti-freezing abilities of different
                  ingredients, we use the FPDF value.{" "}
                  <UnderlineLink
                    to="/ingredients/$slug"
                    params={{ slug: "usdaff-746784" }}
                  >
                    Sucrose (table sugar)
                  </UnderlineLink>{" "}
                  is benchmarked at 100% FPDF. Another commonly-used sugar,{" "}
                  <UnderlineLink
                    to="/ingredients/$slug"
                    params={{ slug: "dextrose" }}
                  >
                    dextrose
                  </UnderlineLink>
                  , has more powerful anti-freezing properties with an FPDF of
                  190%.
                </ArticleParagraph>
                <ArticleAside>
                  Most sources display FPDF as a unitless value (100 for
                  sucrose, 190 for dextrose), but here it&apos;s shown as a
                  percentage to clarify that the value is a multiplier against
                  sucrose.
                </ArticleAside>
              </ArticleSection>

              <ArticleSection>
                <ArticleSectionHeader id="what-kind-of-ingredients-affect-fpdf">
                  What kind of ingredients affect FPDF?
                </ArticleSectionHeader>
                <ArticleList>
                  <ArticleListItem>
                    <strong>Sugars</strong> have differing effects on FPDF
                    depending on size of the molecule.
                    <ArticleList className="pl-4">
                      <ArticleListItem>
                        <UnderlineLink
                          to="/ingredients/$slug"
                          params={{ slug: "dextrose" }}
                        >
                          <strong>Dextrose</strong>
                        </UnderlineLink>{" "}
                        (a form of glucose) is commonly used in ice cream
                        recipes because it has nearly double the anti-freezing
                        power of sucrose and is slightly less sweet.
                      </ArticleListItem>
                      <ArticleListItem>
                        <UnderlineLink
                          to="/ingredients/$slug"
                          params={{ slug: "allulose" }}
                        >
                          <strong>Allulose</strong>
                        </UnderlineLink>{" "}
                        is a popular sugar substitute because it has similar
                        properties to dextrose, but only a tenth of the
                        calories. Be careful not to exceed {formatGrams(20)} per
                        serving, because excessive amounts can cause digestive
                        issues (
                        <ExternalLink
                          className="text-md"
                          href="https://pmc.ncbi.nlm.nih.gov/articles/PMC6315886/"
                        >
                          source
                        </ExternalLink>
                        ).
                      </ArticleListItem>
                    </ArticleList>
                  </ArticleListItem>
                  <ArticleListItem>
                    <UnderlineLink
                      to="/ingredients/$slug"
                      params={{ slug: "usdaff-746775" }}
                    >
                      <strong>Salt</strong>
                    </UnderlineLink>{" "}
                    is a powerful anti-freezing agent (over{" "}
                    {formatPercentage(10)}), but you can&apos;t add too much
                    before making it too salty. Up to {formatPercentage(0.001)}{" "}
                    of the total recipe weight is a good starting point, which
                    will enhance the flavor and lower the freezing point.
                  </ArticleListItem>
                  <ArticleListItem>
                    <strong>Alcohol</strong>, such as{" "}
                    <UnderlineLink
                      to="/ingredients/$slug"
                      params={{ slug: "usdafndds-2710704" }}
                    >
                      <strong>vodka</strong>
                    </UnderlineLink>
                    , is one of the strongest freezing point depressants (740%)
                    and is commonly used to soften a recipe in small quantities.
                  </ArticleListItem>
                </ArticleList>

                <ArticleAside>
                  <p>
                    If you want to dive deeper into the science, check out these
                    resources:
                  </p>
                  <div className="mt-2 space-y-1">
                    <p>
                      <ExternalLink href="https://medium.com/@gelatologist/what-pac-is-and-how-to-calculate-it-2f1ade1bd5df">
                        What PAC is and how to calculate it (Gelatologist)
                      </ExternalLink>
                    </p>
                    <p>
                      <ExternalLink href="https://under-belly.org/sugars-in-ice-cream/">
                        Sugars in ice cream (Underbelly)
                      </ExternalLink>
                    </p>
                  </div>
                </ArticleAside>
              </ArticleSection>
            </ArticleBody>
          </Article>

          <Suspense fallback={null}>
            <FpdfCalculator />
          </Suspense>
        </div>
      </ArticleContainer>
    </PageContainer>
  );
}
